import { useState } from "react";
import axios from "axios";
import { Send, Mail, MapPin, Phone, CheckCircle2 } from "lucide-react";
import { Reveal } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "Collaboration", message: "" });
  const [status, setStatus] = useState(null); // 'loading' | 'ok' | 'err'

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("ok");
      setForm({ name: "", email: "", subject: "Collaboration", message: "" });
    } catch {
      setStatus("err");
    }
  };

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <main data-testid="contact-page" className="relative pt-24" style={{ background: "var(--coal)", minHeight: "100vh" }}>
      <section className="relative px-5 md:px-16 py-16 md:py-24">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 md:gap-20">
          {/* Left intro */}
          <Reveal>
            <span className="st-eyebrow">Reach Soul Trip</span>
            <h1 className="mt-3 st-cinzel font-black text-[var(--cream)] leading-[0.92]" style={{ fontSize: "clamp(2.4rem, 6vw, 5rem)" }}>
              Let's
              <em className="block not-italic st-cormorant italic font-light text-[var(--gold-lt)]" style={{ fontSize: "clamp(1.6rem, 4.2vw, 3.5rem)", marginTop: 6 }}>
                begin a journey.
              </em>
            </h1>
            <div className="st-rule mt-5"/>
            <p className="mt-6 st-infant text-base md:text-lg text-[var(--fog)] leading-relaxed">
              Brand collaborations, distribution, press, or simply a story you think we should chase —
              the inbox is always open. We read everything.
            </p>

            <div className="mt-10 space-y-5">
              {[
                { Icon: Mail,    label: "Email",  v: "gandabherundaarts@gmail.com" },
                { Icon: MapPin,  label: "Base",   v: "Hyderabad · Telangana · India" },
                { Icon: Phone,   label: "Studio", v: "Gandabherunda Arts · By Appointment" },
              ].map(({ Icon, label, v }) => (
                <div key={label} className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 flex items-center justify-center shrink-0"
                    style={{ border: "1px solid rgba(201,160,74,.35)", background: "rgba(201,160,74,.06)" }}
                  >
                    <Icon size={14} className="text-[var(--gold)]"/>
                  </div>
                  <div>
                    <div className="st-cinzel text-[0.5rem] tracking-[0.35em] uppercase text-[var(--gold)]">{label}</div>
                    <div className="st-infant text-[1rem] text-[var(--cream)] mt-1">{v}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 p-6" style={{ background: "rgba(201,160,74,.05)", border: "1px solid rgba(201,160,74,.18)" }}>
              <div className="st-cinzel text-[0.5rem] tracking-[0.35em] uppercase text-[var(--gold)]">Response Time</div>
              <p className="mt-2 st-infant text-[0.95rem] text-[var(--fog)] leading-relaxed">
                We're often in the field, on a mountain, or chasing a sunrise — replies take 48–72 hrs.
                Patience tastes like Maredumilli coffee.
              </p>
            </div>
          </Reveal>

          {/* Right form */}
          <Reveal delay={120}>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="relative p-6 md:p-10"
              style={{ background: "rgba(8,6,4,.7)", backdropFilter: "blur(12px)", border: "1px solid rgba(201,160,74,.18)" }}
            >
              <div className="st-cinzel text-[0.5rem] tracking-[0.45em] uppercase text-[var(--gold)]">
                ✦ Send Transmission
              </div>
              <h2 className="mt-2 st-cormorant italic text-2xl md:text-3xl text-[var(--cream)]">
                Drop us a line.
              </h2>

              <div className="mt-7 space-y-4">
                <div>
                  <label className="st-cinzel text-[0.5rem] tracking-[0.3em] uppercase text-[var(--gold)] block mb-2">Name</label>
                  <input data-testid="contact-name" required value={form.name} onChange={set("name")} placeholder="Your name" className="st-input"/>
                </div>
                <div>
                  <label className="st-cinzel text-[0.5rem] tracking-[0.3em] uppercase text-[var(--gold)] block mb-2">Email</label>
                  <input data-testid="contact-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@domain.com" className="st-input"/>
                </div>
                <div>
                  <label className="st-cinzel text-[0.5rem] tracking-[0.3em] uppercase text-[var(--gold)] block mb-2">Subject</label>
                  <select
                    data-testid="contact-subject"
                    value={form.subject}
                    onChange={set("subject")}
                    className="st-input"
                  >
                    <option>Collaboration</option>
                    <option>Distribution / Streaming</option>
                    <option>Press &amp; Media</option>
                    <option>A Story Idea</option>
                    <option>Just Saying Hello</option>
                  </select>
                </div>
                <div>
                  <label className="st-cinzel text-[0.5rem] tracking-[0.3em] uppercase text-[var(--gold)] block mb-2">Message</label>
                  <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={set("message")} placeholder="Tell us the story..." className="st-input resize-none"/>
                </div>
              </div>

              <button
                data-testid="contact-submit"
                type="submit"
                disabled={status === "loading"}
                className="mt-7 st-btn st-btn-solid st-interactive w-full justify-center"
              >
                {status === "loading" ? "Sending…" : <>Send Message <Send size={11}/></>}
              </button>

              {status === "ok" && (
                <div data-testid="contact-success" className="mt-5 flex items-start gap-3 p-4" style={{ background: "rgba(201,160,74,.08)", border: "1px solid rgba(201,160,74,.3)" }}>
                  <CheckCircle2 size={16} className="text-[var(--gold-lt)] mt-0.5 shrink-0"/>
                  <div>
                    <div className="st-cinzel text-[0.5rem] tracking-[0.35em] uppercase text-[var(--gold)]">Transmission Received</div>
                    <div className="st-infant text-[0.95rem] text-[var(--fog)] mt-1">We'll write back as soon as we're out of the woods.</div>
                  </div>
                </div>
              )}
              {status === "err" && (
                <div data-testid="contact-error" className="mt-5 p-4 st-infant text-[#e89090]" style={{ background: "rgba(232,90,90,.08)", border: "1px solid rgba(232,90,90,.3)" }}>
                  Something interrupted the signal. Please try again.
                </div>
              )}
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
